import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  signIn,
  signOut,
  confirmSignIn,
} from 'aws-amplify/auth';
import { Card, Form, Input, Button, Alert, Typography, message } from 'antd';
import { LockOutlined, UserOutlined, KeyOutlined } from '@ant-design/icons';
import { useAuth, useMunicipalityFromHost } from '@/hooks';
import { authApi, getErrorMessage, isHandledAuthError, translateCognitoError } from '@/services';
import { isLocalDevLoginEnabled, saveLocalDevAuth, saveSessionVersion } from '@/services/auth/localDev';
import { getEnvTheme } from '@/config/envTheme';

type Step =
  | 'login'
  | 'newPassword'
  | 'forgotPassword'
  | 'resetPassword'
  | 'mfaCode';

// backend の login-session / report-login-failure が 423 LOCKED を返したかを判定
const isLockedResponse = (err: unknown): boolean => {
  return axios.isAxiosError(err) && err.response?.status === 423;
};
const LOCKED_MESSAGE = 'アカウントがロックされています。管理者にお問い合わせください。';

const LoginScreen = () => {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const { login } = useAuth();
  const fromHost = useMunicipalityFromHost();
  const localDevLoginEnabled = isLocalDevLoginEnabled();
  const [form] = Form.useForm();
  const [newPasswordForm] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [mfaCodeForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 「あと 1 回でロック」の注意書きを出すか。残り 1 回（4 回目失敗）に達したときだけ true。
  const [showLockWarning, setShowLockWarning] = useState(false);
  const [step, setStep] = useState<Step>('login');
  const [pendingLoginId, setPendingLoginId] = useState('');
  // resetPassword フロー: 確認コード送信先 (Cognito が * でマスクしたメールアドレスを返す)
  const [resetUsername, setResetUsername] = useState('');
  const [resetDestination, setResetDestination] = useState('');
  // EMAIL MFA フロー: Cognito がコード送信先 (メールアドレス) を * マスクで返す
  const [mfaDestination, setMfaDestination] = useState('');

  const completeLogin = async () => {
    const { data: sessionData } = await authApi.loginSession();
    if (sessionData.sessionVersion != null) {
      saveSessionVersion(sessionData.sessionVersion);
    }
    login({
      cognitoSub: sessionData.cognitoSub,
      municipality: sessionData.municipality || '',
      municipalityId: sessionData.municipalityId ?? null,
      municipalitySubdomain: sessionData.municipalitySubdomain ?? null,
      ocrUse: sessionData.ocrUse ?? false,
      loginId: sessionData.loginId,
      displayName: sessionData.displayName,
      role: sessionData.role,
      roleDisplay: sessionData.roleDisplay ?? '',
      permissions: sessionData.permissions ?? [],
      sessionVersion: sessionData.sessionVersion,
    });
  };

  /**
   * Cognito の signIn / confirmSignIn 結果を受けて、次の step に遷移する共通処理。
   * MFA challenge / TOTP setup / 新パスワード等を一箇所で振り分ける。
   */
  const handleSignInResult = async (
    result: Awaited<ReturnType<typeof signIn>>,
    fallbackLoginId?: string,
  ) => {
    if (result.isSignedIn) {
      await completeLogin();
      return;
    }

    const next = result.nextStep;
    if (!next) {
      setError(t('loginIncomplete'));
      return;
    }

    if (fallbackLoginId) setPendingLoginId(fallbackLoginId);

    switch (next.signInStep) {
      case 'CONFIRM_SIGN_UP':
        setError(t('confirmRequired'));
        return;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setStep('newPassword');
        return;
      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
        const dest = (next as { codeDeliveryDetails?: { destination?: string } }).codeDeliveryDetails?.destination ?? '';
        setMfaDestination(dest);
        setStep('mfaCode');
        mfaCodeForm.resetFields();
        return;
      }
      default:
        // EMAIL MFA 以外の challenge は本プロジェクトでは想定外
        // (Cognito UserPool 側で MFA = Required + Email only に設定済み)
        setError(t('loginIncomplete'));
        return;
    }
  };

  /**
   * Cognito 経路でログイン失敗を backend に best-effort で報告する。
   * 5 回連続失敗で backend 側が自動ロックする。
   * LocalDev 経路では backend がカウント済みなのでスキップ。
   * 「失敗」とみなさない error 名はカウント対象外として除外する。
   *
   * @returns locked = この失敗で新たにロックされたか（= 5 回目。6 回目を待たずロック通知する）。
   *   lastChance = あと 1 回でロックされる状態か（= 4 回目・残り 1 回。注意書きを出す）。
   */
  const reportFailureIfApplicable = async (
    loginId: string,
    err: unknown,
  ): Promise<{ locked: boolean; lastChance: boolean }> => {
    const none = { locked: false, lastChance: false };
    if (localDevLoginEnabled) return none;
    const e = (err ?? {}) as { name?: string; message?: string };
    // MFA 未登録エラーはパスワード検証「後」に出る（=認証情報は正しい）。
    // アカウント設定の不備なので、ログイン失敗としてカウント／ロックしない。
    if (typeof e.message === 'string' && /no mfa mechanism/i.test(e.message)) return none;
    const excluded = new Set([
      'UserAlreadyAuthenticatedException',
      'PasswordResetRequiredException',
      'UserNotConfirmedException',
      'LimitExceededException',
      'InvalidPasswordException',
    ]);
    if (e.name && excluded.has(e.name)) return none;
    try {
      const { data } = await authApi.reportLoginFailure(loginId);
      return { locked: data?.locked === true, lastChance: data?.lastChance === true };
    } catch { /* best-effort */ return none; }
  };

  const submit = async (values: { loginId: string; password: string }) => {
    setError('');
    setShowLockWarning(false);
    const loginId = String(values.loginId).trim();
    const password = values.password;

    setLoading(true);
    try {
      // ログインボタン押下直後の pre-check。DB 上で既にロック済みのユーザは
      // Cognito / dev-login を呼ぶ前に弾き、ID/PW 段階でロック通知する。
      // (Cognito disable と DB locked_at に不整合がある場合に MFA まで進んでしまうのを防ぐ)
      try {
        await authApi.checkAccountLock(loginId);
      } catch (err: unknown) {
        if (isLockedResponse(err)) {
          setError(LOCKED_MESSAGE);
          return;
        }
        // pre-check 自体のネットワーク失敗等は無視し、本来のログイン処理に進む。
      }

      if (localDevLoginEnabled) {
        const { data } = await authApi.devLogin(loginId, password);
        login(saveLocalDevAuth(data));
        return;
      }

      const credentials = { username: loginId, password };
      let result: Awaited<ReturnType<typeof signIn>>;
      try {
        result = await signIn(credentials);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
          await signOut();
          result = await signIn(credentials);
        } else {
          throw err;
        }
      }

      await handleSignInResult(result, loginId);
    } catch (err: unknown) {
      if (localDevLoginEnabled) {
        if (isHandledAuthError(err)) {
          return;
        }
        if (isLockedResponse(err)) {
          setError(LOCKED_MESSAGE);
          return;
        }
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError(t('incorrectCredentials'));
          return;
        }
        setError(getErrorMessage(err, t('incorrectCredentials')));
      } else {
        if (isHandledAuthError(err)) {
          return;
        }
        if (isLockedResponse(err)) {
          // completeLogin (loginSession) が 423 を返したケース
          setError(LOCKED_MESSAGE);
          return;
        }
        // Cognito sign-in 失敗を backend に報告 (best-effort)。
        // 報告は await してから既存の error 表示処理に進む。
        const { locked: justLocked, lastChance } = await reportFailureIfApplicable(loginId, err);
        await signOut().catch(() => undefined);
        if (justLocked) {
          // この失敗（5 回目）がロックの引き金。6 回目を待たずここでロック通知する。
          setError(LOCKED_MESSAGE);
        } else {
          // 残り 1 回（4 回目失敗）のときだけロック予告の注意書きを出す。
          setShowLockWarning(lastChance);
          const translated = translateCognitoError(err, 'signIn');
          if (translated) {
            setError(translated.message);
          } else if (axios.isAxiosError(err)) {
            // backend 由来のエラーは日本語メッセージを持つのでそれを表示。
            setError(getErrorMessage(err, t('loginFailed')));
          } else {
            // 未対応の Cognito 生エラー（英語）はそのまま表示せず、汎用の日本語にフォールバックする。
            setError(t('loginFailed'));
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const submitForgotPassword = async (values: { loginId: string }) => {
    setError('');
    setLoading(true);
    const loginId = String(values.loginId).trim();
    try {
      await authApi.forgotPassword(loginId);
      setResetUsername(loginId);
      // バックエンドはアカウント存在を漏らさないため送信先メールは返さない。ヘッダーは汎用文言にフォールバックする。
      setResetDestination('');
      setStep('resetPassword');
      resetForm.resetFields();
    } catch (err: unknown) {
      if (isHandledAuthError(err)) return;
      setError(getErrorMessage(err, 'パスワードリセットの開始に失敗しました。しばらくしてから再度お試しください。'));
    } finally {
      setLoading(false);
    }
  };

  const submitResetPassword = async (values: { code: string; newPassword: string; confirmPassword: string }) => {
    setError('');
    setLoading(true);
    try {
      await authApi.resetPassword(resetUsername, String(values.code).trim(), values.newPassword);
      message.success('パスワードを変更しました。新しいパスワードでログインしてください。');
      setStep('login');
      setResetUsername('');
      setResetDestination('');
      forgotForm.resetFields();
      resetForm.resetFields();
      form.resetFields();
    } catch (err: unknown) {
      if (isHandledAuthError(err)) return;
      setError(getErrorMessage(err, 'パスワードの変更に失敗しました。確認コードと新しいパスワードをご確認ください。'));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    setError('');
    setLoading(true);
    try {
      const result = await confirmSignIn({
        challengeResponse: values.newPassword,
        options: { userAttributes: { name: pendingLoginId } },
      });
      await handleSignInResult(result);
    } catch (err: unknown) {
      if (isHandledAuthError(err)) return;
      if (isLockedResponse(err)) {
        setError(LOCKED_MESSAGE);
        return;
      }
      await signOut().catch(() => undefined);
      const translated = translateCognitoError(err, 'confirmSignIn');
      setError(translated ? translated.message : getErrorMessage(err, t('loginFailed')));
      setStep('login');
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  /**
   * EMAIL MFA コード入力。
   * メールで届いた 6 桁コードを confirmSignIn に渡す。
   */
  const submitMfaCode = async (values: { code: string }) => {
    setError('');
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: String(values.code).trim() });
      await handleSignInResult(result);
    } catch (err: unknown) {
      if (isHandledAuthError(err)) return;
      if (isLockedResponse(err)) {
        setError(LOCKED_MESSAGE);
        return;
      }
      const translated = translateCognitoError(err, 'confirmSignIn');
      const errName = (err as { name?: string })?.name ?? '';
      // signInStore / Cognito SESSION 期限切れの場合、MFA 画面に留まっても
      // 再送信は通らない (Cognito 側で session が無効化済み)。ログイン画面に戻す。
      const sessionDead = errName === 'SignInException'
        || errName === 'NotAuthorizedException'
        || errName === 'ExpiredCodeException';
      if (sessionDead) {
        await signOut().catch(() => undefined);
        setStep('login');
        setMfaDestination('');
        setPendingLoginId('');
        mfaCodeForm.resetFields();
        form.resetFields();
      }
      setError(translated ? translated.message : getErrorMessage(err, '認証コードが正しくありません。'));
    } finally {
      setLoading(false);
    }
  };

  const headerText = (() => {
    if (step === 'newPassword') return t('setNewPasswordDesc');
    if (step === 'forgotPassword') return 'パスワードをリセットします。ログインIDを入力してください。';
    if (step === 'resetPassword') return resetDestination
      ? `${resetDestination} に確認コードを送信しました。コードと新しいパスワードを入力してください。`
      : '確認コードと新しいパスワードを入力してください。';
    if (step === 'mfaCode') return mfaDestination
      ? `${mfaDestination} に送信した確認コードを入力してください。`
      : 'メールで送信した確認コードを入力してください。';
    return tc('pleaseLogin');
  })();

  const envTheme = getEnvTheme();
  const wrapper = (children: React.ReactNode) => (
    <div className={`min-h-screen bg-gradient-to-br ${envTheme.pageGradient} flex flex-col items-center justify-center p-6`}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Typography.Title level={3} className="!mb-2">{tc('appName')}</Typography.Title>
          {fromHost.name && (
            <Typography.Text strong className="block !text-base !mb-1">{fromHost.name}</Typography.Text>
          )}
          <Typography.Text type="secondary">{headerText}</Typography.Text>
        </div>
        <Card>{children}</Card>
      </div>
    </div>
  );

  const cancelAndBackToLogin = async () => {
    // 進行中の signIn セッションを破棄してからログイン画面に戻す。
    // signOut しないと次回の signIn で UserAlreadyAuthenticatedException が出るケースがある。
    await signOut().catch(() => undefined);
    setStep('login');
    setError('');
    setMfaDestination('');
    form.resetFields();
  };

  if (step === 'mfaCode') {
    return wrapper(
      <Form form={mfaCodeForm} layout="vertical" onFinish={submitMfaCode} className="customer-validation-form" autoComplete="off">
        {error && (
          <Form.Item>
            <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />
          </Form.Item>
        )}
        <Form.Item
          name="code"
          label="認証コード"
          rules={[
            { required: true, whitespace: true, message: '認証コードを入力してください。' },
            { pattern: /^\d{6,8}$/, message: '6 桁の数字を入力してください。' },
          ]}
        >
          <Input
            prefix={<KeyOutlined />}
            autoComplete="one-time-code"
            placeholder="123456"
            disabled={loading}
            size="large"
            inputMode="numeric"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            {loading ? '確認中...' : '認証'}
          </Button>
        </Form.Item>
        <Form.Item className="!mb-0 text-center">
          <Button type="link" onClick={cancelAndBackToLogin} disabled={loading}>
            ログイン画面に戻る
          </Button>
        </Form.Item>
      </Form>
    );
  }

  if (step === 'forgotPassword') {
    return wrapper(
      <Form form={forgotForm} layout="vertical" onFinish={submitForgotPassword} className="customer-validation-form" autoComplete="off">
        {error && (
          <Form.Item>
            <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />
          </Form.Item>
        )}
        <Form.Item
          name="loginId"
          label={t('loginId')}
          rules={[{ required: true, whitespace: true, message: t('loginRequired') }]}
        >
          <Input
            prefix={<UserOutlined />}
            autoComplete="off"
            placeholder={t('loginIdPlaceholder')}
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            {loading ? '送信中...' : '確認コードを送信'}
          </Button>
        </Form.Item>
        <Form.Item className="!mb-0 text-center">
          <Button type="link" onClick={() => { setStep('login'); setError(''); }} disabled={loading}>
            ログイン画面に戻る
          </Button>
        </Form.Item>
      </Form>
    );
  }

  if (step === 'resetPassword') {
    return wrapper(
      <Form form={resetForm} layout="vertical" onFinish={submitResetPassword} className="customer-validation-form" autoComplete="off">
        {error && (
          <Form.Item>
            <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />
          </Form.Item>
        )}
        <Form.Item
          name="code"
          label="確認コード"
          rules={[{ required: true, whitespace: true, message: '確認コードを入力してください。' }]}
        >
          <Input
            prefix={<KeyOutlined />}
            autoComplete="one-time-code"
            placeholder="メールに届いた確認コード"
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={t('newPassword')}
          rules={[
            { required: true, message: t('passwordRequired') },
            { min: 8, message: 'パスワードは8文字以上入力してください。' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!/[a-z]/.test(value)) return Promise.reject('英小文字を含めてください。');
                if (!/[A-Z]/.test(value)) return Promise.reject('英大文字を含めてください。');
                if (!/\d/.test(value)) return Promise.reject('数字を含めてください。');
                if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) return Promise.reject('記号を含めてください。');
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            placeholder={t('passwordPlaceholder')}
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={t('confirmPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('passwordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            placeholder={t('passwordPlaceholder')}
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            {loading ? '変更中...' : 'パスワードを変更'}
          </Button>
        </Form.Item>
        <Form.Item className="!mb-0 text-center">
          <Button type="link" onClick={() => { setStep('login'); setError(''); }} disabled={loading}>
            ログイン画面に戻る
          </Button>
        </Form.Item>
      </Form>
    );
  }

  if (step === 'newPassword') {
    return wrapper(
      <Form form={newPasswordForm} layout="vertical" onFinish={submitNewPassword} className="customer-validation-form">
        {error && (
          <Form.Item>
            <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />
          </Form.Item>
        )}
        <Form.Item
          name="newPassword"
          label={t('newPassword')}
          rules={[
            { required: true, message: t('passwordRequired') },
            { min: 8, message: 'パスワードは8文字以上入力してください。' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!/[a-z]/.test(value)) return Promise.reject('英小文字を含めてください。');
                if (!/[A-Z]/.test(value)) return Promise.reject('英大文字を含めてください。');
                if (!/\d/.test(value)) return Promise.reject('数字を含めてください。');
                if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) return Promise.reject('記号を含めてください。');
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            placeholder={t('passwordPlaceholder')}
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={t('confirmPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('passwordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            placeholder={t('passwordPlaceholder')}
            disabled={loading}
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            {loading ? t('settingPassword') : t('setNewPassword')}
          </Button>
        </Form.Item>
      </Form>
    );
  }

  return wrapper(
    <Form form={form} layout="vertical" onFinish={submit} className="customer-validation-form" autoComplete="off">
      {localDevLoginEnabled && (
        <Form.Item>
          <Alert
            message="ローカル開発ログイン"
            description="ID: admin / パスワード: Admin123"
            type="info"
            showIcon
          />
        </Form.Item>
      )}
      {error && (
        <Form.Item>
          <Alert
            message={error}
            description={showLockWarning ? t('lockoutWarning') : undefined}
            type="error"
            showIcon
            closable
            onClose={() => { setError(''); setShowLockWarning(false); }}
          />
        </Form.Item>
      )}
      <Form.Item
        name="loginId"
        label={t('loginId')}
        rules={[{ required: true, whitespace: true, message: t('loginRequired') }]}
      >
        <Input
          prefix={<UserOutlined />}
          autoComplete="off"
          placeholder={t('loginIdPlaceholder')}
          disabled={loading}
          size="large"
        />
      </Form.Item>
      <Form.Item
        name="password"
        label={t('password')}
        rules={[{ required: true, message: t('passwordRequired') }]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          autoComplete="new-password"
          placeholder={t('passwordPlaceholder')}
          disabled={loading}
          size="large"
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {loading ? t('loggingIn') : t('login')}
        </Button>
      </Form.Item>
      {!localDevLoginEnabled && (
        <Form.Item className="!mb-0 text-center">
          <Button
            type="link"
            onClick={() => { setStep('forgotPassword'); setError(''); }}
            disabled={loading}
          >
            パスワードをお忘れですか？
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default LoginScreen;
